"use client";

/**
 * Setup Phase Context
 * 
 * Computes the user's current setup phase from their auth state and provides
 * progressive UI unlock signals to layout components.
 * 
 * Phases:
 *   0 - No display name set (still using email prefix). Show LogoOnly variant.
 *   1 - Has display name set, but no brand created yet. Show FullEnvironment, no tabs.
 *   2 - Has brand created, but plan is "inactive". Show FullEnvironment with tabs, features locked.
 *   3 - Active subscription (any paid plan or "dev"). Full access.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import type { OrgPlan } from "@vastipost/shared-types";

export type SetupPhase = 0 | 1 | 2 | 3;

export interface SetupPhaseState {
	/** Current setup phase (0-3) */
	phase: SetupPhase;
	/** Whether setup is fully complete (phase 3) */
	isSetupComplete: boolean;
	/** Whether the top navigation bar should be shown */
	showHeader: boolean;
	/** Whether the bottom tab bar should be shown */
	showBottomNav: boolean;
	/** Whether the environment is LogoOnly (phase 0) */
	isLogoOnly: boolean;
	/** Whether the user needs to set their display name */
	needsDisplayName: boolean;
	/** Whether the user needs to create a brand */
	needsBrand: boolean;
	/** Whether the user needs an active subscription */
	needsSubscription: boolean;
}

const SetupPhaseContext = createContext<SetupPhaseState>({
	phase: 3,
	isSetupComplete: true,
	showHeader: true,
	showBottomNav: true,
	isLogoOnly: false,
	needsDisplayName: false,
	needsBrand: false,
	needsSubscription: false,
});

/**
 * Plans that grant full access (phase 3)
 */
const ACTIVE_PLANS: Set<OrgPlan> = new Set([
	"free", "starter", "professional", "business", "enterprise", "dev",
]);

/**
 * Check if the user's display name is still the default email prefix
 * We consider it "not set" if it matches the email prefix
 */
function isDisplayNameDefault(name: string | undefined, email: string | undefined): boolean {
	if (!name || !email) return true;
	const emailPrefix = email.split("@")[0];
	return name === emailPrefix;
}

export function SetupPhaseProvider({ children }: { children: ReactNode }) {
	const auth = useAuth();

	const state = useMemo<SetupPhaseState>(() => {
		// Not authenticated — treat as full access (the auth guard handles redirects)
		if (!auth?.isAuthenticated || !auth.user || !auth.org) {
			return {
				phase: 3,
				isSetupComplete: true,
				showHeader: true,
				showBottomNav: true,
				isLogoOnly: false,
				needsDisplayName: false,
				needsBrand: false,
				needsSubscription: false,
			};
		}

		const hasDisplayName = !isDisplayNameDefault(auth.user.name, auth.user.email);
		const hasBrand = auth.brand !== null;
		const hasActiveSubscription = ACTIVE_PLANS.has(auth.org.plan);

		// If user has a brand, they completed onboarding (signup-complete creates brand).
		// Skip the display-name check — a name matching the email prefix is valid
		// if the user explicitly chose it during signup.
		if (!hasDisplayName && !hasBrand) {
			return {
				phase: 0,
				isSetupComplete: false,
				showHeader: true,
				showBottomNav: false,
				isLogoOnly: true,
				needsDisplayName: true,
				needsBrand: !hasBrand,
				needsSubscription: !hasActiveSubscription,
			};
		}

		if (!hasBrand) {
			return {
				phase: 1,
				isSetupComplete: false,
				showHeader: true,
				showBottomNav: false,
				isLogoOnly: false,
				needsDisplayName: false,
				needsBrand: true,
				needsSubscription: !hasActiveSubscription,
			};
		}

		if (!hasActiveSubscription) {
			return {
				phase: 2,
				isSetupComplete: false,
				showHeader: true,
				showBottomNav: true,
				isLogoOnly: false,
				needsDisplayName: false,
				needsBrand: false,
				needsSubscription: true,
			};
		}

		return {
			phase: 3,
			isSetupComplete: true,
			showHeader: true,
			showBottomNav: true,
			isLogoOnly: false,
			needsDisplayName: false,
			needsBrand: false,
			needsSubscription: false,
		};
	}, [auth]);

	return (
		<SetupPhaseContext.Provider value={state}>
			{children}
		</SetupPhaseContext.Provider>
	);
}

/**
 * Hook to access setup phase state
 */
export function useSetupPhase(): SetupPhaseState {
	return useContext(SetupPhaseContext);
}

/**
 * Hook to check if a feature should be locked
 * Features require at least phase 3 (active subscription)
 */
export function useIsFeatureLocked(): boolean {
	const { phase } = useSetupPhase();
	return phase < 3;
}
